import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import ArchiveRecordLabel from 'archive/components/ArchiveRecordLabel';
import isArchived from 'archive/isArchived';
import {DataModificationsHeader} from 'audit/DataModifications';
import {ProcessesFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import WorkspaceItemInaccessibleGuard from 'auth/WorkspaceItemInaccessibleGuard';
import BufferOptions from 'buffers/BufferOptions';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {
  ActionMethod,
  ActionOrigin,
  ProcessPlatformFeatureType,
  ProcessAsyncRecordStatus,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import MetadataTagTable from 'metadata/MetadataTagTable';
import notifications from 'notifications';
import {asyncBulkUpdateProcessWithDataMods} from 'processes/dashboard/bulk-edit/ProcessTaskEditorDropdown';
import {isDirectExecutionTaskWithNoOutputSchema} from 'processes/dashboard/ExecuteTasksButton';
import FixFailedRoutingAndMappingModal from 'processes/modals/fix-failed-routing-and-mapping/FixFailedRoutingAndMappingModal';
import {
  canEditSchemaFieldValues,
  getProcessPlatformFeatureConfig,
} from 'processes/processes-platform/FeatureOptions';
import ProcessRealtimeUpdateSubscriptionWrapper from 'processes/ProcessRealtimeUpdateSubscriptionWrapper';
import submitTasksWithConfirmation from 'processes/tables/submitTasksWithConfirmation';
import getTestProps from 'test/lib/cypress/getTestProps';
import InlineBanner, {InlineBannerActionType, InlineBannerType} from 'ui-library/banners/InlineBanner';
import * as Headers from 'ui-library/Headers';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import runModal from 'ui-library/runModal';
import pick from 'util/pick';

import {
  ProcessTaskDetailsQuery,
  ProcessTaskDetailsQueryVariables,
} from './graphql/__generated/ProcessTaskDetailsQuery';
import {PROCESS_TASK_DETAILS_QUERY} from './graphql/GetProcessTask';
import {ProcessTaskDetailsMetadata, ProcessTaskDetailsSummary} from './ProcessTaskDetailsSections';
import ProcessTaskActionToolbar from './toolbar/ProcessTaskActionToolbar';
import {updateProcessTaskDetailsOutputsQueryCache} from './utils/updateTaskDetailsOutputs';
import {
  updateProcessTaskAndCache,
  updateProcessTaskDetailsQueryCache,
  UpdateTaskParams,
} from './utils/updateTaskDetailsTask';

type ProcessTaskGql = ExtractByTypename<ProcessTaskDetailsQuery['processTask'], 'ProcessTaskNode'>;
type ProcessAsyncRecordGql = ExtractByTypename<
  ProcessTaskGql['processAsyncRecords'],
  'ProcessAsyncRecordNode'
>;

interface QueryingProcessTaskDetailsProps {
  processTaskId: string;
}

interface ProcessDetailsHeaderProps {
  processTask: ProcessTaskGql;
  refetch: () => Promise<unknown>;
}
interface ProcessTaskDetailsProps {
  processTask: ProcessTaskGql;
  refetch: () => Promise<unknown>;
}

function getFailedMappings(processTask: ProcessTaskGql): Array<ProcessAsyncRecordGql> {
  return (processTask.processAsyncRecords as Array<ProcessAsyncRecordGql>).filter(
    ({status}) => status === ProcessAsyncRecordStatus.FAILED
  );
}

export default function QueryingProcessTaskDetails(
  props: QueryingProcessTaskDetailsProps
): JSX.Element | null {
  const result = useBenchlingQuery<ProcessTaskDetailsQuery, ProcessTaskDetailsQueryVariables>(
    PROCESS_TASK_DETAILS_QUERY,
    {
      variables: {processTaskId: props.processTaskId},
      fetchPolicy: 'cache-and-network',
      renderCachedDataWhenRefetching: true,
    }
  );
  if (result.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  } else if (result.type === QueryResultType.ERROR) {
    notifications.error('Could not load task; try reloading the page.');
    return null;
  }

  const {processTask} = result.data;
  return !isInaccessible(processTask) ? (
    <ProcessRealtimeUpdateSubscriptionWrapper
      apiId={processTask.id}
      refetch={result.refetch}
      messageFilter="no_filter"
    >
      <ProcessTaskDetails processTask={processTask} refetch={result.refetch} />
    </ProcessRealtimeUpdateSubscriptionWrapper>
  ) : (
    <WorkspaceItemInaccessibleGuard
      inaccessibleInfo={processTask}
      modelType={ModelTypeConstants.PROCESS_TASK}
    />
  );
}

function ProcessTaskDetailsHeader(props: ProcessDetailsHeaderProps): JSX.Element {
  return (
    <div className={css(styles.headingWrapper)}>
      <div>
        <Headers.Header1>{props.processTask.name}</Headers.Header1>
        <ArchiveRecordLabel
          item={{
            id: props.processTask.id,
            name: props.processTask.name,
            archiveRecord: props.processTask.archiveRecord,
          }}
        />
      </div>
      <div className="u-small-padding-right">
        <ProcessTaskActionToolbar
          processTask={props.processTask}
          onTaskUpdated={props.refetch}
          updateCache={updateProcessTaskDetailsQueryCache}
          updateOutputsCache={updateProcessTaskDetailsOutputsQueryCache}
        />
      </div>
    </div>
  );
}

function ProcessTaskDetails(props: ProcessTaskDetailsProps): JSX.Element {
  const {processTask} = props;
  const showDirectExecutionNoOutputBanner = isDirectExecutionTaskWithNoOutputSchema(processTask.schema);
  const failedProcessAsyncRecords: Array<ProcessAsyncRecordGql> = getFailedMappings(processTask);
  const showFixFailedMappingsBanner = failedProcessAsyncRecords.length > 0;

  const onUpdateTask = async (updatedTask: UpdateTaskParams): Promise<void> => {
    const updated = await updateProcessTaskAndCache(updatedTask);
    // When the user changes the task status, the new value must be synced with the status in the outputs tab.
    if (updated?.status != null) {
      updateProcessTaskDetailsOutputsQueryCache({
        taskId: processTask.id,
        updatedStatusAndOutputs: {status: pick(updated.status, '__typename', 'id')},
      });
    }
  };
  return (
    <>
      {isArchived(props.processTask) && <BufferOptions banner="Editing disabled on archived task." />}
      <div
        className="react-tab-container"
        {...getTestProps({component: 'ProcessTaskDetails', element: 'root'})}
      >
        {showDirectExecutionNoOutputBanner && (
          <div className="u-small-padding-bottom">
            <InlineBanner
              bannerType={InlineBannerType.INFO}
              text="To execute this task, edit its details and status below"
            />
          </div>
        )}
        <ProcessTaskDetailsHeader processTask={props.processTask} refetch={props.refetch} />
        {showFixFailedMappingsBanner && (
          <div className="u-small-padding-bottom">
            <InlineBanner
              bannerType={InlineBannerType.ERROR}
              text="Fix mapping errors to continue this workflow."
              action={{
                actionType: InlineBannerActionType.CUSTOM,
                actionText: 'View & fix error(s)',
                actionOnClick: async () => {
                  await runModal(FixFailedRoutingAndMappingModal, {
                    selectedId: processTask.id,
                    onRequestClose: async () => {
                      await props.refetch();
                    },
                  });
                },
              }}
            />
          </div>
        )}
        <div className={css(styles.detailsWrapper)}>
          <ProcessTaskDetailsSummary processTask={processTask} onUpdateTask={onUpdateTask} />
          <ProcessTaskDetailsMetadata processTask={processTask} onUpdateTask={onUpdateTask} />
        </div>
        {getProcessPlatformFeatureConfig(
          ProcessPlatformFeatureType.ARE_SCHEMA_FIELDS_VISIBLE,
          props.processTask.schema.executionType,
          props.processTask.schema.name
        ) ? (
          <MetadataTagTable
            title="Schema Fields"
            emptyMessage="This task has no fields."
            fileId={props.processTask.id}
            initialTags={props.processTask.fields}
            areTagsEditable={
              isFeatureEnabled({feature: ProcessesFeature.PROCESSES_EDIT, item: processTask}) &&
              canEditSchemaFieldValues(processTask.schema)
            }
            showRequiredFieldErrors={true}
            updateTags={(inputData) =>
              new Promise((resolve) => {
                submitTasksWithConfirmation({
                  taskSchemaId: processTask.schema.id,
                  tasks: [
                    {
                      id: processTask.id,
                      fields: inputData.toSet,
                      sourceType: 'task_details',
                      oldStatusId: processTask.status.id,
                      actionOrigin: ActionOrigin.WORKFLOWS_TASK_DETAILS_PAGE,
                      actionMethod: ActionMethod.BUTTON_CLICK,
                    },
                  ],
                  onRequestSubmit: (): Promise<void> =>
                    asyncBulkUpdateProcessWithDataMods(
                      [processTask.id],
                      async (dataModsHeader?: DataModificationsHeader) => {
                        const updated = await updateProcessTaskAndCache(
                          {
                            id: processTask.id,
                            fields: inputData.toSet,
                          },
                          dataModsHeader
                        );
                        resolve(
                          updated && [
                            {
                              id: props.processTask.id,
                              tags: updated.fields,
                            },
                          ]
                        );
                      },
                      ActionOrigin.WORKFLOWS_TASK_DETAILS_PAGE
                    ),
                }).then(() => {
                  resolve(null);
                });
              })
            }
          />
        ) : null}
      </div>
    </>
  );
}

const styles = StyleSheet.create({
  headingWrapper: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  detailsWrapper: {
    display: 'flex',
    flexFlow: 'row wrap',
    marginLeft: '-10px',
  },
});
